<template class="cmp-table">
  <div class="cmp-table_wrapper">
    <div ref="gridHeader" style="position: relative;">
      <div v-if="rowSelection" class="cmp-table_select-all-checkbox">
        <CheckBox
            @input="setSelectAllRows($event.target.checked)">
        </CheckBox>
      </div>
      <div draggable="false"
           v-for="(col, index) in customColumns"
           class="cmp-table_table-head"
           @dragstart="handleDragStart($event, col)"
           @dragover="handleDragover($event)"
           @drop="handleDrop($event, col)"
           @click="clearHighlightedRow"
           :key="col.id">
        <div :style="{width: extendLastColumn(customColumns, index) + 'px'}" class="cmp-table_table-cell cmp-table_header">{{ col.title }}
        </div>
      </div>
      <div :class="{'cmp-table_data-wrapper' : localData.length > 0}" :style="{maxHeight:height + 'px'}">
        <div data-cy="txtDataGridNoData"
             v-if="localData.length === 0"
             class="cmp-table_no-data"><i class="fa-solid fa-database"></i><span style="margin-left: 5px">No Data</span>
        </div>
        <div class="cmp-table_row" v-for="(row, index) in pagedData" :key="index" @click="rowClick($event, row, index)">
          <template v-for="(column, colIndex) in customColumns">
            <div id="slot" v-if="typeof $slots[column.key] !== 'undefined'" :key="colIndex"
                 :style="{width: column.width + 'px'}"
                 class="cmp-table_table-cell"
                 :class="{'cmp-table_selected-row' : row.highlightSelectedRow === true && selectable}">
              <slot :name="column.key" :column="column" :grid="row" :rowData="rowDataSource(index)"
                    :rowIndex="index"></slot>
              <div style="position: absolute; right: 0; top:12px;">
                <i v-if="row.showMoreInfo && checkIfMoreInfo(row) && colIndex===1" @click="openMoreInfo(row)"
                   class="fa fa-arrow-circle-o-up cmp-table_more-info-icon"></i>
                <i v-else-if="checkIfMoreInfo(row) && colIndex==1" @click="openMoreInfo(row)"
                   class="fa fa-arrow-circle-o-down cmp-table_more-info-icon"></i>
              </div>
            </div>
            <div v-else
                 :class="{'cmp-table_selected-row' : row.highlightSelectedRow === true  && selectable,'cmp-table_read-only' : column.readOnly && row.readOnly}"
                 :data-cy="dataCy + 'ColumnIndex' + colIndex"
                 :style="{width: column.width + 'px', background: setCellColor(column, row), color: setFontColor(column, row)}"
                 :key="index"
                 class="cmp-table_table-cell">
                <span
                    :class="{'cmp-table_selected-row' : row.highlightSelectedRow === true  && selectable}"
                    style="border: none; background: none;" :data-cy="dataCy +'RowIndex' + index" :title="rowData(row, column.key)"
                    :style="{width: column.width -10 + 'px'}">{{ rowData(row, column.key, column) }}
                </span>
              <div style="position: absolute; right: 2px; top:0;">
                <i v-if="row.showMoreInfo && checkIfMoreInfo(row) && colIndex===1" @click="openMoreInfo(row)"
                   class="fa fa-caret-down cmp-table_more-info-icon-down"></i>
                <i v-else-if="checkIfMoreInfo(row) && colIndex===1" @click="openMoreInfo(row)"
                   class="fa fa-caret-down cmp-table_more-info-icon"></i>
              </div>
            </div>
          </template>
          <transition name="slide">
            <div v-if="row.showMoreInfo" class="cmp-table_more-detail">
              <span style="font-size: 16px; font-weight: bold;">{{ getColumnMoreInfo(row.moreInfo.field) }}</span>
              <div>{{ row[row.moreInfo.field] }}</div>
            </div>
          </transition>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import CheckBox from "@/components/common/input/CheckBox";
import MovingTooltip from "@/components/common/other/MovingTooltip";
import Button from "@/components/common/input/Button";

export default {
  name: "DataGrid",

  components: {
    CheckBox,
    MovingTooltip,
    Button,

  },

  props: {

    currentPage: {
      type: Number,
      default: 1
    },

    currentPaginationRange: {
      type: Number
    },

    pageSize: {
      type: Number,
      default: 100
    },

    allowGridMenu: {
      type: Boolean,
      default: true
    },

    allowPagination: {
      type: Boolean,
      default: true
    },

    removeHighlightedRow: {
      type: Boolean,
      default: false
    },

    selectable: {
      type: Boolean,
      default: true
    },

    columns: Array,

    rowSelection: {
      type: Boolean,
      default: true
    },

    dataSource: {
      type: Array,
      default: function () {
        return []
      }
    },

    dataCy: String,

    height: {
      type: String,
      default: '500px'
    },

  },

  emits: ['update:currentPaginationRange', 'update:currentPage', 'clickReset', 'clickSave'],

  data() {

    return {

      sourceColumnIndex: null,
      colHeight: 40,
      localData: [],
      openMenuDrop: false,
      allowReOrderColumns: false,
      sortOrder: true,
      columnIndex: null,
      currentColumnCount: 0,

      paginationArray: [],
      paginationSize: 10,

    }
  },

  computed: {

    setFooterPoint() {
      return parseInt(this.height)
    },

    customColumns() {
      return this.columns.filter(c => c.enabled === true)
    },

    pagedData: function () {

      if (this.localData.length > 0) {

        return this.localData.filter((row, index) => {

          // eg if page size set to 10, and current page is 2 (default)
          let self = this;
          let startIndex = (self.currentPage - 1) * self.pageSize; // 1 X 10 = 10   / start = 10
          let endIndex = self.currentPage * self.pageSize; // 2 X 10 = 20   / end = 20

          // if the current index >= 10 (start), and the index is less then 20 (end), return the array from index 10 to 19  = 10 records inclusive (page size!)
          if (index >= startIndex && index < endIndex) {
            return true
          }

        });
      }

    },

  },

  methods: {

    // setTop() {
    //
    //   if(this.$refs.gridHeader) {
    //     const gridTop = this.$refs.gridHeader.getBoundingClientRect().top -window.scrollY;
    //     return gridTop - 28 + 'px'
    //
    //   }
    //
    // },


    createPages() {

      this.paginationArray = [];

      let numberOfPages = this.localData.length / this.pageSize;
      let roundUp = Math.ceil(numberOfPages);

      for (let i = 0; i < roundUp; i++) {

        let newPageButton = {
          number: i + 1,
          notValid: false
        }

        this.paginationArray.push(newPageButton);
      }

    },

    splitPagination() {

      return this.paginationArray.filter((row, index) => {

        let startIndex = (this.currentPaginationRange - 1) * this.paginationSize;
        let endIndex = this.currentPaginationRange * this.paginationSize;

        if (index >= startIndex && index < endIndex) {
          return true
        }

      });

    },

    pageUp() {

      if(this.currentPaginationRange < this.getPaginationPages()) {

        let val = this.currentPaginationRange + 1
        this.$emit('update:currentPaginationRange', val)
      }

    },

    pageDown() {

      if(this.currentPaginationRange > 1) this.$emit('update:currentPaginationRange', this.currentPaginationRange - 1)

    },

    getPaginationPages() {

      return  Math.ceil(this.paginationArray.length / this.paginationSize);
    },

    handleDragStart(e, sourceContainerData) {

      let mainArraySourceIndex = this.columns.findIndex(i => i.id === sourceContainerData.id)
      mainArraySourceIndex === 0 ? e.preventDefault() : this.sourceColumnIndex = mainArraySourceIndex;
    },

    handleDragover(e) {
      e.preventDefault();
      return false;
    },

    handleDrop(e, destinationColumnData) {
      e.stopPropagation();

      let mainArrayDestinationIndex = this.columns.findIndex(i => i.id === destinationColumnData.id)

      if (this.sourceColumnIndex !== mainArrayDestinationIndex && mainArrayDestinationIndex !== 0) {

        if (mainArrayDestinationIndex < this.sourceColumnIndex) {

          this.columns.splice(mainArrayDestinationIndex, 0, this.columns[this.sourceColumnIndex]);
          this.columns.splice(this.sourceColumnIndex + 1, 1);

        } else {
          this.columns.splice(mainArrayDestinationIndex + 1, 0, this.columns[this.sourceColumnIndex]);
          this.columns.splice(this.sourceColumnIndex, 1);

        }

      }
    },

    resetColumns() {
      this.columns.forEach(function (item) {
        item.enabled = true;
      })
      this.columns.sort((a, b) => {
        return a.id - b.id
      });

    },

    setSortCondition(index, column) {
      if (index > 0 && typeof (this.dataSource[0][column.key]) !== 'boolean' && column.sortable) {
        return true;
      }
    },

    sortColumns(column) {
      if (column) {

        // Sort order uses true for ascending / false for descending
        if (typeof (this.dataSource[0][column.key]) !== 'boolean' && column.sortable) {

          this.sortOrder = !this.sortOrder;

          if (this.sortOrder) {
            this.localData.sort((a, b) => a[column.key].localeCompare(b[column.key]))

          } else {
            this.localData.sort((a, b) => b[column.key].localeCompare(a[column.key]))
          }

        }

      }
    },

    openMenu() {
      this.openMenuDrop = !this.openMenuDrop;

      this.openMenuDrop ? this.$refs.gridMenu.style.cssText = "visibility: visible; opacity: 1;" :
          this.$refs.gridMenu.style.cssText = "visibility: hidden; opacity: 0;";
    },

    rowDataSource(index) {
      return this.dataSource[index];
    },

    setCellColor(column, row) {
      // Return the css color value eg(#345321) held in the row data using the matching prop set in the column array
      if (column.cellColorProp) return row[column.cellColorProp];
    },

    setFontColor(column, row) {
      if (row[column.cellColorProp]) return '#fff'
    },

    checkIfMoreInfo(row) {
      if (row.hasOwnProperty('moreInfo')) {
        return row.moreInfo.show;
      }
    },

    setSelectAllRows(boolValue) {
      this.localData.forEach(item => {

        item.checkRow = boolValue;
      });

      boolValue ? this.$emit('allRowsSelected', this.dataSource) : this.$emit('allRowsSelected', []);

    },

    clearHighlightedRow() {
      for (let ea in this.localData) {
        this.localData[ea]['highlightSelectedRow'] = false;
      }

    },

    extendLastColumn(columns, index) {
      let lastColumn = columns.length - 1;

      if (index === lastColumn) {

        let width = parseInt(columns[lastColumn].width)
        let extendedWidth = width + 12;
        // console.log(this.currentColumnCount + 'Extended')
        return extendedWidth.toString();
      }

      return columns[index].width

    },

    getColumnMoreInfo(info) {
      return this.columns.find(x => x.key === info).title
    },

    rowClick(e, row, index) {

      this.clearHighlightedRow();
      row.highlightSelectedRow = true;

      if (this.selectable && !row.checkRow) this.$emit('selected-row', this.dataSource[index], index);

    },

    openMoreInfo(row) {
      row.showMoreInfo = !row.showMoreInfo;
    },

    rowData(row, key, column) {

      // Old code to check if row has a dropdown object (text & value)

      // if (row[key] && row[key].hasOwnProperty('text')) {
      //
      //   if (column) {
      //
      //     if (column.maxChars && row[key].text) return row[key].text.substring(0, column.maxChars)
      //         + this.checkStringLength(row[key], column.maxChars);
      //
      //   }
      //   return row[key].text;
      // }

      if (column) {

        // Get the row data from the past in key from the current column
        if (typeof (row[key]) === 'string') {

          if (column.maxChars && row[key]) return row[key].substring(0, column.maxChars)
              + this.checkStringLength(row[key], column.maxChars);

        }

      }

      return row[key];
    },

    checkStringLength(characters, maxChars) {

      if (characters) {

        if (characters.trim().length > maxChars) {
          return '...'
        }
      }
      return '';
    },

    setNewObjectProp() {

      if (this.localData.length > 0) {

        this.localData.forEach((item) => {
          // item['showMoreInfo'] = false;
          item['checkRow'] = false;

        });

      }
    }

  },

  watch: {

    dataSource() {

      this.localData = JSON.parse(JSON.stringify(this.dataSource));
      this.setNewObjectProp();
      this.clearHighlightedRow();
      this.createPages();
    },

    removeHighlightedRow() {

      this.clearHighlightedRow();
    }

  },

  mounted: function () {

    // Break the observer reference and create a copy in local data
    this.localData = JSON.parse(JSON.stringify(this.dataSource));
    this.setNewObjectProp();
    this.createPages();

  }
}
</script>

<style lang="scss" scoped>

span {
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

.cmp-table {

  &_wrapper {
    width: max-content;
    //font-size: 13px;
    position: relative;
    //margin-top: 20px;

  }

  &_data-wrapper {
    position: absolute;
    top: 36px;
    overflow-y: scroll;
    overflow-x: hidden;
    overscroll-behavior-y: none;
    border-bottom: 1px solid #f1f1f1;
    //box-shadow: 1px 1px 4px 0px #acacac;
  }

  &_selected-row {
    background: #f9efe0;
    /*color: #0e0e0e;*/
  }

  &_read-only {
    color: #a0a0a0;
    background-color: #fcfcfc;
  }

  &_header {
    height: 26px;
    z-index: 99;
  }

  &_table-head {
    display: inline-block;
    box-shadow: 1px 1px 2px 0px #9b9b9b;
    font-size: 13px;
    //font-weight: bold;
  }

  &_table-head * {
    background: #f8f8f8;
    //background-image: linear-gradient(0deg, #eaeaea, #fff);
    color: #000000 !important;
    border-bottom: 1px solid #eaeaea;
    border-top: 1px solid #f3f3f3;
    border-left:  1px solid #e3e3e3;
    font-size: 13px!important;
    //font-weight: bold;
  }

  &_table-cell, &_table-cell-slot {
    position: relative;
    //border: 1px solid #d4d4d4;
    float: left;
    padding: 4px 5px 5px 6px;
    /*width: 28%;*/
    margin-bottom: -1px;
    margin-right: -1px;
    display: grid;
    //line-height: 1.8;
    align-items: center;
    overflow: hidden;
    border-left:  1px solid #eaeaea;
    font-size: 12px;
    color: #232323;
  }

  &_select-all-checkbox {
    position: absolute;
    top: 5px;
    left: 7px;
    z-index: 100;
  }

  &_column-menu-footer {
    margin-top: 20px;
    display: inline-flex;
    gap: 5px;
  }

  &_no-data {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: -4px;
    height: 100px;
    background-color: #f8f8f8;
    color: #7c7c7c;
    font-size: 17px;
  }

  &_more-info-icon {
    font-size: 24px;
    margin-top: 2px;
  }

  &_more-detail {
    margin-top: 29px;
    height: 100px;
    border-left: 1px solid #d4d4d4;
    max-width: fit-content;
    padding: 6px;
  }

  &_row {
    display: flex;
    background-color: #ffffff;
    //height: 28px;
    font-size: 12px;
  }

  &_row:nth-child(even) {
    background-color: #fafafa;
  }

  &_sort-column-arrows {
    top: 0;
    padding-left: 4px;
  }

  &_slot-items {
    margin-right: 60px;
  }

}

</style>
